import React, { useEffect, useState } from 'react';
import Shared from 'screens/shared';
import { actions, ACTIONS_TYPES } from 'redux/local';
import { useSelector } from 'react-redux';
import { _t, locations, objectToOptions } from 'utils';
import {
  VALIDATION, CPU_FAMILY, AVAILABILITY_ZONE, STORAGE_TYPE, DAYS,
} from 'constant';


export default ({
  isCancel = false,
  onCancel,
  nodepool: nodepoolEdit = {},
}) => {
  const [selectedDataCenterId, setSelectedDataCenterId] = useState(nodepoolEdit?.properties?.datacenterId || '');
  const [isNodeCountRange, setIsNodeCountRange] = useState(false);
  const [nodeCountRange, setNodeCountRange] = useState([1, 10]);
  const [privateLans, setPrivateLans] = useState([]);
  const [nodeCount, setNodeCount] = useState(1);

  const { cluster = {}, version = {}, dataCenter = {} } = useSelector((e) => e);
  const { id: clusterId = '', properties: clusterProperties = {} } = cluster;
  const { k8sVersion: clusterVersion } = clusterProperties;
  const { compatibilities = [] } = version;

  const { properties: dcProperties = {} } = dataCenter;
  const { cpuArchitecture = [] } = dcProperties;

  let { [ACTIONS_TYPES.DATA_CENTER.GET_ALL]: dataCenters = [] } = useSelector((e) => e);
  dataCenters = dataCenters.map(({ id, properties: { location, name } }) => (
    [
      id,
      <div className="locations-dropdown">
        <img src={locations[location].image} alt={name} />
        <div>
          <span>{name}</span>
          <br />
          <span style={{
            marginTop: -5,
            display: 'block',
            fontSize: 10,
          }}
          >
            {`${locations[location].name} (${location})`}
          </span>
        </div>
      </div>,
    ]
  ));
  const { id, properties = {} } = nodepoolEdit;
  if (properties.maintenanceWindow) {
    properties.dayOfTheWeek = properties.maintenanceWindow.dayOfTheWeek;
    properties.time = properties.maintenanceWindow.time;
    delete properties.maintenanceWindow;
  }

  const { maxNodeCount = 0, minNodeCount = 0 } = properties.autoScaling || {};

  if (maxNodeCount || minNodeCount) {
    properties.isAutoScaling = true;
    properties.nodeCountRange = [minNodeCount, maxNodeCount];
    setIsNodeCountRange(true);
    delete properties.autoScaling;
  }

  if (!privateLans.length && properties.lans && Array.isArray(properties.lans)) {
    properties.privateLans = properties.lans.map((item) => ({ ...item, lanid: item.id }));
    setPrivateLans(properties.privateLans);
    delete properties.lans;
  }


  const noodpool = {
    name: '',
    dayOfTheWeek: 'Monday',
    time: '12:00:00',
    nodeCount: 1,
    coresCount: 1,
    ramSize: 2,
    storageSize: 10,
    availabilityZone: AVAILABILITY_ZONE.AUTO,
    nodeCountRange,
    ...properties,
  };

  const noodpoolFields = [
    {
      name: 'pool-setting',
      type: 'label',
      title: (
        <>
          {_t('Pool Settings')}
          <Shared.widgets.IconPopup
            iconName="question-circle"
            content={(
              <p>
                <strong>{_t('Pool Settings')}</strong>
                <br />
                <br />
                Here you set the size and location of your node pool. You can provision node pools only inside of existing data centers.
                <br />
                <br />
                The pool name can not be changed once the pool has been created. The size of the pool can be adjusted at any time.
              </p>
            )}
            iconStyle={{ marginLeft: 10, fontSize: 15 }}
          />
        </>
      ),
    },
    {
      name: 'name',
      type: 'text',
      title: _t('Name'),
      visible: !id,
    },
    {
      name: 'datacenterId',
      type: 'dropdown',
      title: _t('Data Center'),
      onChange: (dcId) => {
        setSelectedDataCenterId(dcId);
        setPrivateLans([]);
      },
      values: [],
      value: selectedDataCenterId,
      disabled: !!id,
    },
    {
      name: 'k8sVersion',
      type: 'dropdown',
      title: 'Node Pool Version',
    },
    {
      name: 'isAutoScaling',
      type: 'switch',
      title: 'Node Count Autoscaling',
      onChange: (value) => setIsNodeCountRange(value),
    },
    {
      name: 'nodeCountRange',
      title: 'Node Count Range',
      type: 'range-basic',
      onChange: (value) => {
        const [min = 0, max = 0] = value;
        if (min < 1 || min > 99 || min > max) return 0;
        if (max > 100 || max < 1) return 0;
        setNodeCount((s) => {
          if (min > s) return min;
          if (s > max) return max;
          return s;
        });
        return setNodeCountRange(value);
      },
    },
    {
      name: 'nodeCount',
      title: _t('Node Count'),
      type: 'slider',
      range: [1, 100],
      showValue: true,
      onChange: (e) => setNodeCount(e),
    },
    {
      name: 'privateLans',
      title: _t('KUBERNETES.NODE_POOL.FIELDS.PRIVATE_LANS'),
      type: 'array',
      icon: 'ethernet',
      onChange: (value) => setPrivateLans(value),
      setItemTitle: (e) => `LAN: ${e?.lanid}`,
      setItemDetails: (e) => `DHCP: ${e.dhcp ? '✓' : '✗'}, ${_t('KUBERNETES.NODE_POOL.FIELDS.ROUTES')}: ${e.routes?.length || 0}`,
      fields: [
        {
          name: 'lanid',
          title: 'LAN',
          type: 'lan',
          hasNewOption: false,
          privateOnly: true,
        },
        {
          name: 'dhcp',
          title: 'DHCP',
          type: 'switch',
        },
        {
          name: 'routes',
          title: _t('KUBERNETES.NODE_POOL.FIELDS.ROUTES'),
          type: 'array',
          isAddButton: true,
          actionButtonName: _t('KUBERNETES.NODE_POOL.HELP_TEXTS.ADD_ROUTE'),
          // setItemTitle: (e) => e?.name,
          setItemDetails: (e) => (`Network: ${e.network} Gateway IP: ${e.gatewayIp}`),
          fields: [
            {
              name: 'network',
              title: 'Network',
              type: 'text',
              helpText: 'Format: CIDR Notation',
            },
            {
              name: 'gatewayIp',
              title: 'Gateway IP',
              type: 'text',
              helpText: 'Format: IPv4',
            },
          ],
          validations: [
            { name: 'gatewayIp', type: VALIDATION.REQUIRED },
            { name: 'gatewayIp', type: VALIDATION.IP_V4 },
            { name: 'network', type: VALIDATION.REQUIRED },
            { name: 'network', type: VALIDATION.IP4_CIDR },
          ],
        },
      ],
      validations: [
        { name: 'lanid', type: VALIDATION.REQUIRED },
      ],
    },
    {
      name: 'time',
      type: 'time',
      title: _t('Maintenance time'),
    },
    {
      name: 'dayOfTheWeek',
      type: 'dropdown',
      title: _t('Maintenance day'),
      values: DAYS,
    },
    {
      name: 'node-template',
      type: 'label',
      title: (
        <>
          {_t('Node Template')}
          <Shared.widgets.IconPopup
            iconName="question-circle"
            content={(
              <p>
                <strong>{_t('Node Template')}</strong>
                <br />
                <br />
                The node pool will consist of hosts as defined below while the pool size will determine how many of these hosts will be created.
              </p>
            )}
            iconStyle={{ marginLeft: 10, fontSize: 15 }}
          />
        </>
      ),
      visible: !id,
    },
    {
      name: 'availabilityZone',
      type: 'dropdown',
      title: _t('Availability Zone'),
      options: [...objectToOptions(AVAILABILITY_ZONE)],
      visible: !id,
    },

    {
      name: 'cpuFamily',
      type: 'dropdown',
      title: _t('CPU Architecture'),
      visible: !id,
    },

    {
      name: 'coresCount',
      title: _t('Cores'),
      type: 'slider',
      range: [1, 100],
      showValue: true,
      visible: !id,
    },

    {
      name: 'ramSize',
      title: _t('RAM'),
      type: 'slider',
      range: [2, 200],
      showValue: true,
      visible: !id,
    },

    {
      name: 'storageType',
      type: 'dropdown',
      title: _t('Storage Type'),
      options: [...objectToOptions(STORAGE_TYPE, [STORAGE_TYPE.DAS])],
      visible: !id,
    },

    {
      name: 'storageSize',
      title: _t('Storage Size'),
      type: 'slider',
      range: [10, 1000],
      showValue: true,
      visible: !id,
    },
  ];

  const validations = [
    {
      name: 'name',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'name',
      type: VALIDATION.STRING,
      min: 3,
    },
    {
      name: 'name',
      type: VALIDATION.STRING,
      max: 63,
    },
    {
      name: 'name',
      type: VALIDATION.VARIABLE,
    },
    {
      name: 'datacenterId',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'storageType',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'cpuFamily',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'nodeCount',
      type: VALIDATION.REQUIRED,
    },
    {
      name: 'nodeCount',
      type: VALIDATION.NUMBER,
    },
    {
      name: 'k8sVersion',
      type: VALIDATION.REQUIRED,
    },
  ];

  useEffect(() => {
    actions.dataCenter.getAll({});
  }, []);

  useEffect(() => {
    if (clusterVersion) {
      actions
        .kubernetes
        .version
        .getCompatibilities({ clusterVersion });
    }
    // eslint-disable-next-line
  }, [clusterVersion]);


  useEffect(() => {
    if (selectedDataCenterId) {
      actions.dataCenter.get({ id: selectedDataCenterId });
    }
  }, [selectedDataCenterId]);

  const onSave = ({
    name,
    k8sVersion,
    time,
    dayOfTheWeek,
    // eslint-disable-next-line no-shadow
    nodeCount,
    coresCount,
    ramSize,
    datacenterId,
    availabilityZone,
    cpuFamily,
    storageType,
    storageSize,
    isAutoScaling,
    // eslint-disable-next-line no-shadow
    nodeCountRange,
    // eslint-disable-next-line no-shadow
    privateLans = [],
  }) => {
    const extraProps = {};

    if (isAutoScaling) {
      extraProps.autoScaling = {
        minNodeCount: nodeCountRange[0],
        maxNodeCount: nodeCountRange[1],
      };
    }

    if (Array.isArray(privateLans) && privateLans.length) {
      extraProps.lans = privateLans.map((item) => ({ ...item, id: item.lanId })).map((item) => delete item.lanId);
    }

    if (id) {
      actions.kubernetes.nodepool.update({
        id,
        clusterId,
        properties: {
          nodeCount,
          k8sVersion,
          maintenanceWindow: {
            time,
            dayOfTheWeek,
          },
          ...extraProps,
        },
      });
      if (onCancel) onCancel();
    } else {
      actions.kubernetes.nodepool.create({
        clusterId,
        properties: {
          name,
          k8sVersion,
          maintenanceWindow: {
            dayOfTheWeek,
            time,
          },
          datacenterId,
          nodeCount,
          cpuFamily,
          coresCount,
          availabilityZone,
          storageType,
          storageSize,
          ramSize: ramSize * 1024,
          ...extraProps,
        },
      });
    }
  };

  return (
    <>
      <Shared.forms.Form
        fields={noodpoolFields}
        fieldsValues={noodpool}
        changeFields={[
          {
            name: 'datacenterId',
            props: { values: dataCenters, value: selectedDataCenterId },
          },
          {
            name: 'k8sVersion',
            props: { values: compatibilities },
          },
          {
            name: 'cpuFamily',
            props: {
              options: cpuArchitecture.map(({ cpuFamily }) => [
                cpuFamily,
                <span>
                  <img
                    src={CPU_FAMILY[cpuFamily].logo}
                    style={{ height: 18, marginRight: 10, verticalAlign: 'middle' }}
                    alt={CPU_FAMILY[cpuFamily].name}
                  />
                  {CPU_FAMILY[cpuFamily].name}
                </span>]),
            },
          },
          {
            name: 'nodeCountRange',
            props: { value: nodeCountRange, visible: isNodeCountRange },
          },
          {
            name: 'nodeCount',
            props: { range: isNodeCountRange ? nodeCountRange : [1, 100], value: nodeCount },
          },
          {
            name: 'privateLans',
            props: { value: privateLans },
          },
          {
            name: 'lanid',
            props: {
              lanFilter: (item) => !privateLans.find((lan) => item.lanid === lan.lanid),
            },
          },
        ]}
        onSubmit={onSave}
        isCancel={isCancel}
        onCancel={onCancel}
        validations={validations}
      />
    </>
  );
};
